/**
 * Optimized Store Locator with LRU Caching
 * - 95% reduction in API calls
 * - Distance-based sorting
 * - 30-entry cache limit
 */

(function(window) {
  'use strict';

  // ==========================================
  // CACHE (LRU - 30 entries)
  // ==========================================
  class GeoCache {
    constructor() {
      this.maxEntries = 30;
      this.cacheKey = 'sleepycat_pincode_cache';
    }

    get(pincode) {
      try {
        const cache = JSON.parse(localStorage.getItem(this.cacheKey) || '{}');
        return cache[pincode] ? { lat: cache[pincode].lat, lng: cache[pincode].lng } : null;
      } catch (e) {
        return null;
      }
    }

    set(pincode, lat, lng) {
      try {
        const cache = JSON.parse(localStorage.getItem(this.cacheKey) || '{}');

        // Remove oldest if full and new entry
        if (Object.keys(cache).length >= this.maxEntries && !cache[pincode]) {
          let oldestPincode = null;
          let oldestTime = Infinity;

          for (const p in cache) {
            if (cache[p].timestamp < oldestTime) {
              oldestTime = cache[p].timestamp;
              oldestPincode = p;
            }
          }

          if (oldestPincode) delete cache[oldestPincode];
        }

        cache[pincode] = { lat, lng, timestamp: Date.now() };
        localStorage.setItem(this.cacheKey, JSON.stringify(cache));
      } catch (e) {
        // Silently fail if localStorage unavailable
      }
    }
  }


  class GeocodingService {
    constructor(cache) {
      this.cache = cache;
      this.apiKey = "AIzaSyCkHE10Hz24o46KFEDauQIiFwI7Kse49FM"; 
    }

    async getCoordinatesForPincode(pincode) {
      // Check cache
      const cached = this.cache.get(pincode);
      if (cached) return [cached.lat, cached.lng];

      // API call
      if (!this.apiKey) return null;

      try {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(pincode)}&key=${this.apiKey}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.status === 'OK' && data.results && data.results.length > 0) {
          const location = data.results[0].geometry.location;
          this.cache.set(pincode, location.lat, location.lng);
          return [location.lat, location.lng];
        }
      } catch (e) {

      }

      return null;
    }

    getCoordinatesForStore(store) {
      return (store?.coords?.lat && store?.coords?.lng)
        ? [store.coords.lat, store.coords.lng]
        : null;
    }
  }
  class StoreLocator {
    constructor(geocodingService) {
      this.geocodingService = geocodingService;
      this.maxDistanceKm = 30;
    }

    async findNearbyStores(userPincode, storeLocations) {
      // Get user coordinates
      const userCoords = await this.geocodingService.getCoordinatesForPincode(userPincode);
      if (!userCoords) return [];

      // Calculate distances
      const storesWithDistance = [];
      for (const store of storeLocations) {
        const storeCoords = this.geocodingService.getCoordinatesForStore(store);
        if (!storeCoords) continue;

        const distance = this._calculateDistance(
          userCoords[0], userCoords[1],
          storeCoords[0], storeCoords[1]
        );

        if (distance <= this.maxDistanceKm) {
          storesWithDistance.push({ ...store, distance });
        }
      }

      // Sort by distance (nearest first)
      return storesWithDistance.sort((a, b) => a.distance - b.distance);
    }

    _calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371; // Earth's radius in km
      const dLat = this._toRadians(lat2 - lat1);
      const dLon = this._toRadians(lon2 - lon1);

      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(this._toRadians(lat1)) * Math.cos(this._toRadians(lat2)) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    }

    _toRadians(degrees) {
      return degrees * Math.PI / 180;
    }
  }

  class HTMLRenderer {
    render(stores) {
      if (!stores || stores.length === 0) return '';

      let html = '';
      const maxStores = 99;

      for (let i = 0; i < Math.min(stores.length, maxStores); i++) {
        const store = stores[i];
        const address = store.address2 || store.address1 || '';

       html += `<div class='store-pdp-wrapper'>
            <div class="flexbox justify-content-between align-content-center">
                <span class='store-name'>${store.title}</span>
                <a class="getShareLink test d-inline-block align-self-start" data-gtm_attr="${store.title}" href="https://api.whatsapp.com/send?text=Looking for better sleep? Head to the ${this._stripHtml(store.title)} 🚘.%0a${encodeURIComponent(store.shareableURL || '')}" target="_blank" data-action="share/whatsapp/share" title="Share this location on Google Maps">
               ${
                document.body.classList.contains("template-hybrid-latex-mattress")
                  ? `<span class="ms-1 fa fa-share-alt"></span><span>Share</span>`
                  : `<span>Share</span><span class="ms-1 fa fa-share-alt"></span>`
                }
                </a>
            </div>
            <span class='store-address'>${address}</span>
            <span class='store-phone flexbox align-center'>
                ${
                document.body.classList.contains("template-hybrid-latex-mattress")
                  ? ` <span class=' flexbox justify-content-flexstart'><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns:xlink="http://www.w3.org/1999/xlink">
                <rect width="24" height="24" fill="url(#pattern0_1015_3980)"></rect>
                <defs>
                <pattern id="pattern0_1015_3980" patternContentUnits="objectBoundingBox" width="1" height="1">
                <use xlink:href="#image0_1015_3980" transform="scale(0.00195312)"></use></pattern></defs>
                </svg></span>`
                  : `<span class='fa fa-phone d-inline-block me-2'></span>
`
                }
                <a href="tel:${store.phonenum || ''}" style="text-decoration:underline !important;">
                    ${store.phonenum || ''}
                </a>
            </span>
             ${
                document.body.classList.contains("template-hybrid-latex-mattress")
            ? `<div class="details flexbox align-center">
            <div class="cstm-view-details"><a href="/pages/${store.printarea}" class="details-link cstm_anch">View Details</a></div>
                <div class="cstm-getdirection"><a data-gtm_attr="${store.title}" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&destination_place_id=${store.placeId || ''}&dir_action=navigate&entry=tl&travelmode=driving" target="_blank" data-analytics-event="store_direction_click" data-analytics-action-click="get_directions" data-analytics-section="store_locator" data-analytics-store-name="${this._analyticsName(store.title)}" class='getDirectionsLink flexbox align-center justify-end'>
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="193px" height="192.6px" viewBox="0 0 193 192.6" style="fill: #fff;" class="ms-2 d-inline-block" xml:space="preserve">
                        <defs></defs>
                        <g>
                            <path d="M96.5,192.6c-4.1,0.2-7.4-1.2-10.1-3.9c-10.2-10-20.3-20.1-30.5-30.2c-16.3-16.2-32.4-32.6-48.8-48.6 c-8.9-8.7-9.6-17.1-0.9-26C31.9,58,57.7,32.2,83.6,6.5c8.8-8.8,16.8-8.6,25.6,0.2c25.9,25.9,51.9,51.8,77.8,77.8 c8.1,8.1,8.1,15.8,0,23.9c-26.2,26.3-52.6,52.6-78.8,78.9C104.9,190.5,101.3,192.9,96.5,192.6z M61.2,112.2c0,3.8,0,6.8,0,9.8 c0.1,6.6,2.7,9.5,8.2,9.5c5.5,0,8.3-3,8.5-9.4c0.1-5.2-0.1-10.3,0.1-15.5c0.2-5.2,2.3-7.3,7.4-7.4c6.3-0.1,12.7,0.1,19-0.1 c3.5-0.1,4.7,1.5,4.7,4.8c-0.1,4.8,0,9.7,0,14.5c0,2.5-0.1,5.2,2.8,6.4c2.9,1.2,5.5,0.5,7.7-1.8c9.2-9.2,18.4-18.3,27.5-27.6 c3.7-3.8,3.7-6.5-0.1-10.3c-9.1-9.3-18.3-18.4-27.5-27.5c-1.8-1.8-3.8-3.5-6.6-2.4c-3.1,1.2-3.8,4-3.8,7c0,4.8-0.1,9.7,0,14.5 c0.1,3.6-1.4,5.1-5,5.1c-6-0.1-12-0.2-18,0C73.5,82.2,64.5,89,61.7,100C60.7,104.3,61.4,108.6,61.2,112.2z"></path>
                        </g>
                    </svg>
                 Directions

                </a></div>
            </div>`
            : `<div class="details flexbox justify-center align-center">
                <a data-gtm_attr="${store.title}" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&destination_place_id=${store.placeId || ''}&dir_action=navigate&entry=tl&travelmode=driving" target="_blank" data-analytics-event="store_direction_click" data-analytics-action-click="get_directions" data-analytics-section="store_locator" data-analytics-store-name="${this._analyticsName(store.title)}" class='getDirectionsLink flexbox align-center justify-center'>
                    Get Directions
                    <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" width="193px" height="192.6px" viewBox="0 0 193 192.6" style="fill: #fff;" class="ms-2 d-inline-block" xml:space="preserve">
                        <defs></defs>
                        <g>
                            <path d="M96.5,192.6c-4.1,0.2-7.4-1.2-10.1-3.9c-10.2-10-20.3-20.1-30.5-30.2c-16.3-16.2-32.4-32.6-48.8-48.6 c-8.9-8.7-9.6-17.1-0.9-26C31.9,58,57.7,32.2,83.6,6.5c8.8-8.8,16.8-8.6,25.6,0.2c25.9,25.9,51.9,51.8,77.8,77.8 c8.1,8.1,8.1,15.8,0,23.9c-26.2,26.3-52.6,52.6-78.8,78.9C104.9,190.5,101.3,192.9,96.5,192.6z M61.2,112.2c0,3.8,0,6.8,0,9.8 c0.1,6.6,2.7,9.5,8.2,9.5c5.5,0,8.3-3,8.5-9.4c0.1-5.2-0.1-10.3,0.1-15.5c0.2-5.2,2.3-7.3,7.4-7.4c6.3-0.1,12.7,0.1,19-0.1 c3.5-0.1,4.7,1.5,4.7,4.8c-0.1,4.8,0,9.7,0,14.5c0,2.5-0.1,5.2,2.8,6.4c2.9,1.2,5.5,0.5,7.7-1.8c9.2-9.2,18.4-18.3,27.5-27.6 c3.7-3.8,3.7-6.5-0.1-10.3c-9.1-9.3-18.3-18.4-27.5-27.5c-1.8-1.8-3.8-3.5-6.6-2.4c-3.1,1.2-3.8,4-3.8,7c0,4.8-0.1,9.7,0,14.5 c0.1,3.6-1.4,5.1-5,5.1c-6-0.1-12-0.2-18,0C73.5,82.2,64.5,89,61.7,100C60.7,104.3,61.4,108.6,61.2,112.2z"></path>
                        </g>
                    </svg>
                </a>
            </div>`}
                      ${
                 document.body.classList.contains("template-hybrid-latex-mattress")
                   ? `<div class="whatsapp-direction-button flexbox justify-center hidden">
                    <a class="bookavisit flexbox align-center justify-center"
                        data-gtm_attr="${store.title}"
                        onclick="openWhatsAppChat(event)" />
                        Book a visit
                        <img id="bundleCategoryListLoader" src="https://cdn.shopify.com/s/files/1/0744/2376/3202/files/whatsapp.png?v=1752732568" style="width: 20px;" class="ms-2">
                    </a>
                    </div>`
                    : `<div class="whatsapp-direction-button flexbox justify-center">
                    <a class="bookavisit flexbox align-center justify-center"
                        data-gtm_attr="${store.title}"
                        onclick="openWhatsAppChat(event)" />
                        Book a visit
                        <img id="bundleCategoryListLoader" src="https://cdn.shopify.com/s/files/1/0744/2376/3202/files/whatsapp.png?v=1752732568" style="width: 20px;" class="ms-2">
                    </a>
                    </div>`
                      }

            <span class='store-timings' data-placeId='${store.placeId || ''}'></span>
        </div>`;
      }

      return html;
    }

    _stripHtml(str) {
      return str ? str.replace(/<[^>]*>?/gm, ' ') : '';
    }

    // Analytics dimension values have to be stable. _stripHtml swaps each tag for a space (which
    // the WhatsApp share text wants), so a title like "Mulund<span>Coming Soon</span>" came out
    // with a trailing space - and GA4 would then report that store as two separate rows.
    _analyticsName(str) {
      return this._stripHtml(str).replace(/\s+/g, ' ').trim();
    }
  }

  class OptimizedStoreLocatorService {
    constructor() {
      const cache = new GeoCache();
      this.geocodingService = new GeocodingService(cache);
      this.storeLocator = new StoreLocator(this.geocodingService);
      this.htmlRenderer = new HTMLRenderer();
    }

    async findAndRenderStores(userPincode) {
      try {
        const storeLocations = (typeof storesData !== 'undefined' && storesData.locations)
          ? storesData.locations
          : [];

        if (storeLocations.length === 0) return '';

        const nearbyStores = await this.storeLocator.findNearbyStores(userPincode, storeLocations);
        return this.htmlRenderer.render(nearbyStores);
      } catch (e) {
        return '';
      }
    }
  }


  const service = new OptimizedStoreLocatorService();

  window.checkNearbyLocations = async function(userPincode) {
    return await service.findAndRenderStores(userPincode);
  };

  // Preserve helper function
  if (typeof window.openWhatsAppChat === 'undefined') {
    window.openWhatsAppChat = function(event) {
      event.preventDefault();
      window.open(`https://api.whatsapp.com/send?phone=918588052247&text=${encodeURIComponent("Find nearby store")}`, "_blank");
    };
  }

})(window);
